/**
 * Σελίδα «Πανελλήνιες»: ο χρήστης διαλέγει φίλτρα και από κάτω δημιουργούνται
 * (στον browser) δύο PDF — ένα με τις εκφωνήσεις των θεμάτων που ταιριάζουν και
 * ένα με τις απαντήσεις τους. Δεδομένα: public/panelladikes.json.
 */
import { MultiSelect, type MsOption } from './panelladikes-multiselect';
import { examKey } from './panelladikes-exam';
import type { PanExercise } from './panelladikes-pdf';

interface Book {
  id: string;
  title: string;
}
interface Chapter {
  book: string;
  number: string;
  title: string;
}
interface Section {
  book: string;
  number: string;
  title: string;
  chapter: string;
}
interface Tag {
  id: number;
  label: string;
}
interface ExerciseType {
  id: string;
  label: string;
}
interface Exam {
  year: number;
  /** 'repeat' = επαναληπτικές πανελλαδικές· διαφορετικά κανονικές. */
  kind?: string;
  title: string;
  pdf: string;
}
interface BookSectionRef {
  book: string;
  section_number: string;
}
interface Exercise extends PanExercise {
  book_sections: BookSectionRef[];
  tag_ids: number[];
}
interface PanData {
  books: Book[];
  chapters: Chapter[];
  sections: Section[];
  tags: Tag[];
  exercise_types: ExerciseType[];
  exams: Exam[];
  exercises: Exercise[];
}

const THEMES = ['Α', 'Β', 'Γ', 'Δ'];
const BOOK_SHORT: Record<string, string> = { main: 'Βιβλίο', supp: 'Συμπλ. υλικό' };
const GREEK_THEME_TO_LATIN: Record<string, string> = { Α: 'A', Β: 'B', Γ: 'G', Δ: 'D' };

function byId<T extends HTMLElement>(id: string): T {
  const node = document.getElementById(id);
  if (node === null) throw new Error('Λείπει το στοιχείο #' + id);
  return node as T;
}

let refresh = (): void => undefined; // ορίζεται παρακάτω· τα dropdown το καλούν σε κάθε αλλαγή
const onFilterChange = (): void => refresh();
const f = {
  q: byId<HTMLInputElement>('flt-search'),
  kind: new MultiSelect(byId('pan-f-kind'), 'Όλες', onFilterChange, 'Όλες'),
  year: new MultiSelect(byId('pan-f-year'), 'Όλα τα έτη', onFilterChange),
  theme: new MultiSelect(byId('pan-f-theme'), 'Όλα τα θέματα', onFilterChange),
  type: new MultiSelect(byId('pan-f-type'), 'Όλοι οι τύποι', onFilterChange),
  sec: new MultiSelect(byId('pan-f-sec'), 'Όλα τα κεφάλαια', onFilterChange),
  tag: new MultiSelect(byId('pan-f-tag'), 'Όλες οι έννοιες', onFilterChange),
};
const showYearEl = byId<HTMLInputElement>('pan-f-showyear');
const showAnsEl = byId<HTMLInputElement>('pan-f-showans');
const answersSectionEl = byId('pan-answers-section');
const summaryEl = byId<HTMLElement>('pan-summary');
const statusEl = byId<HTMLElement>('pan-status');
const resultsEl = byId<HTMLElement>('pan-results');

interface PdfPane {
  frame: HTMLElement;
  download: HTMLAnchorElement;
  busy: HTMLElement;
  url: string | null;
  bytes: Uint8Array | null;
}
function pane(prefix: string): PdfPane {
  return {
    frame: byId<HTMLElement>(prefix + '-frame'),
    download: byId<HTMLAnchorElement>(prefix + '-download'),
    busy: byId<HTMLElement>(prefix + '-busy'),
    url: null,
    bytes: null,
  };
}
const statementsPane = pane('pan-statements');
const answersPane = pane('pan-answers');

/** Αναζήτηση χωρίς διάκριση τόνων/πεζών-κεφαλαίων. */
function norm(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/ς/g, 'σ');
}

let data: PanData;
const tagsById = new Map<number, string>();
const typesById: Record<string, string> = {};

type FacetKey = 'kind' | 'year' | 'theme' | 'type' | 'tag' | 'sec';

const KIND_DEFAULT = ['regular', 'repeat']; // προεπιλογή: και τα δύο είδη εξετάσεων
const kindOf = (ex: { exam_kind?: string }): string => (ex.exam_kind === 'repeat' ? 'repeat' : 'regular');

/**
 * Εξεταστέα ύλη για τις εξετάσεις του 2027 (ΥΠΑΙΘΑ 90176/Δ2/06-07-2026). Το Βιβλίο 2 (supp) ισχύει
 * ολόκληρο· στο Βιβλίο 1 (main) εξετάζονται μόνο οι ενότητες της λίστας (μαζί με τις υποενότητές τους).
 * Εκτός ύλης: 1.5-1.6, 3.8+, 4.2+, κεφ. 5, 6.2, 6.6, κεφ. 11-12, 10.7, 13.3-13.4 και κεφ. 14.
 */
const MAIN_SYLLABUS = new Set([
  '1.1', '1.2', '1.3', '1.4',
  '2.1', '2.2', '2.3', '2.4',
  '3.1', '3.2', '3.3', '3.4', '3.5', '3.6', '3.7',
  '4.1',
  '6.1', '6.3', '6.4', '6.5', '6.7',
  '7.1', '7.2', '7.3', '7.4', '7.5', '7.6', '7.7', '7.8', '7.9', '7.10',
  '8.1', '8.2',
  '9.1', '9.2', '9.3', '9.4',
  '10.1', '10.2', '10.3', '10.4', '10.5', '10.6',
  '13.1', '13.2',
]);

function inSyllabus(book: string, num: string): boolean {
  if (book !== 'main') return true;
  const parts = num.split('.');
  return MAIN_SYLLABUS.has(parts.slice(0, 2).join('.'));
}

function secMatch(ex: Exercise, v: string): boolean {
  const [kind, book, num] = v.split(':');
  return ex.book_sections.some((s) => {
    if (s.book !== book || !inSyllabus(s.book, s.section_number)) return false;
    return kind === 'sec' ? s.section_number === num : s.section_number.split('.')[0] === num;
  });
}

/** `skip`: αγνοεί το φίλτρο ενός dropdown — για να μετράμε τι θα έβγαινε αν άλλαζε η επιλογή του. */
function matches(ex: Exercise, skip?: FacetKey): boolean {
  const kinds = f.kind.values;
  if (skip !== 'kind' && kinds.length > 0 && !kinds.includes(kindOf(ex))) return false;
  const years = f.year.values;
  if (skip !== 'year' && years.length > 0 && !years.includes(String(ex.exam_year))) return false;
  const themes = f.theme.values;
  if (skip !== 'theme' && themes.length > 0 && !themes.includes(ex.theme)) return false;
  const types = f.type.values;
  if (skip !== 'type' && types.length > 0 && !types.includes(ex.exercise_type)) return false;
  const tags = f.tag.values;
  if (skip !== 'tag' && tags.length > 0 && !tags.some((t) => ex.tag_ids.includes(Number(t)))) return false;
  const secs = f.sec.values;
  if (skip !== 'sec' && secs.length > 0 && !secs.some((v) => secMatch(ex, v))) return false;
  const q = norm(f.q.value.trim());
  if (q !== '') {
    const hay = norm(
      [ex.code, String(ex.exam_year), ex.theme, typesById[ex.exercise_type] ?? '', kindOf(ex) === 'repeat' ? 'επαναληπτικες επαναληπτικη' : '']
        .concat(ex.tag_ids.map((id) => tagsById.get(id) ?? ''))
        .concat(
          ex.book_sections.map((s) => {
            const sec = data.sections.find((x) => x.book === s.book && x.number === s.section_number);
            return s.section_number + ' ' + (sec?.title ?? '');
          })
        )
        .join(' ')
    );
    if (!q.split(/\s+/).every((w) => hay.includes(w))) return false;
  }
  return true;
}

// ------------------------------------------------------------ PDF

type PdfModule = typeof import('./panelladikes-pdf');
let pdfModule: PdfModule | null = null;
let fontCache: { sans: ArrayBuffer; sansBold: ArrayBuffer; mono: ArrayBuffer } | null = null;
const sourceCache = new Map<string, ArrayBuffer>();

async function fetchBytes(url: string): Promise<ArrayBuffer> {
  const r = await fetch(url);
  if (!r.ok) throw new Error('HTTP ' + r.status + ' ' + url);
  return r.arrayBuffer();
}

async function ensureAssets(keys: string[]): Promise<{
  mod: PdfModule;
  fonts: NonNullable<typeof fontCache>;
}> {
  pdfModule ??= await import('./panelladikes-pdf');
  fontCache ??= {
    sans: await fetchBytes('./panelladikes/fonts/sans.ttf'),
    sansBold: await fetchBytes('./panelladikes/fonts/sans-bold.ttf'),
    mono: await fetchBytes('./panelladikes/fonts/mono.ttf'),
  };
  for (const key of keys) {
    if (sourceCache.has(key)) continue;
    const exam = data.exams.find((e) => examKey({ exam_year: e.year, exam_kind: e.kind }) === key);
    if (exam !== undefined) sourceCache.set(key, await fetchBytes(exam.pdf));
  }
  return { mod: pdfModule, fonts: fontCache };
}

async function paintPane(p: PdfPane): Promise<void> {
  if (p.bytes === null) {
    p.frame.replaceChildren();
    return;
  }
  const bytes = p.bytes;
  try {
    const view = await import('./panelladikes-view');
    await view.renderPdfInto(p.frame, bytes, () => p.bytes === bytes);
  } catch (err) {
    console.error(err);
    p.frame.replaceChildren(Object.assign(document.createElement('p'), {
      className: 'pan-busy-note',
      textContent: 'Η προεπισκόπηση δεν ήταν διαθέσιμη — χρησιμοποίησε το κουμπί «Λήψη».',
    }));
  }
}

function setPane(p: PdfPane, bytes: Uint8Array | null, filename: string): void {
  if (p.url !== null) URL.revokeObjectURL(p.url);
  p.url = null;
  p.bytes = bytes;
  if (bytes === null) {
    p.frame.replaceChildren();
    return;
  }
  p.url = URL.createObjectURL(new Blob([bytes as BlobPart], { type: 'application/pdf' }));
  p.download.href = p.url;
  p.download.download = filename;
  void paintPane(p);
}

// Ξαναζωγράφισε (στο νέο πλάτος) όταν αλλάζει το μέγεθος του παραθύρου.
let resizeTimer: number | undefined;
let lastWidth = window.innerWidth;
window.addEventListener('resize', () => {
  if (window.innerWidth === lastWidth) return;
  lastWidth = window.innerWidth;
  window.clearTimeout(resizeTimer);
  resizeTimer = window.setTimeout(() => {
    void paintPane(statementsPane);
    void paintPane(answersPane);
  }, 300);
});

function filenameSuffix(): string {
  const parts: string[] = [];
  const years = f.year.values;
  if (years.length > 0 && years.length <= 3) parts.push([...years].sort().join('-'));
  if (f.kind.values.length === 1 && f.kind.values[0] === 'repeat') parts.push('epanaliptikes');
  const themes = f.theme.values.map((t) => GREEK_THEME_TO_LATIN[t] ?? t);
  if (themes.length > 0) parts.push('thema-' + themes.join(''));
  return parts.length > 0 ? parts.join('-') : 'epilegmena';
}

/** «2023 - 2024», «2020, 2022 - 2024»: συνεχόμενα έτη γίνονται εύρος. */
function yearRanges(years: number[]): string {
  const ys = [...new Set(years)].sort((a, b) => a - b);
  const out: string[] = [];
  for (let i = 0; i < ys.length; ) {
    let j = i;
    while (j + 1 < ys.length && ys[j + 1] === ys[j]! + 1) j++;
    out.push(j > i ? ys[i] + ' - ' + ys[j] : String(ys[i]));
    i = j + 1;
  }
  return out.join(', ');
}

/** Η γραμμή φίλτρων που γράφεται κάτω από τον τίτλο των PDF. */
function filterSubtitle(items: Exercise[]): string {
  const MAX = 4;
  const list = (labels: string[]): string =>
    labels.length > MAX ? labels.slice(0, MAX).join(', ') + ' + ακόμα ' + (labels.length - MAX) : labels.join(', ');
  const clean = (l: string): string => l.replace(/\s*\(\d+\)$/, '').replace(/^Όλο το κεφάλαιο /, 'Κεφ. ');

  const parts: string[] = [];
  if (f.kind.values.length === 1) parts.push('Εξετάσεις: ' + (f.kind.values[0] === 'repeat' ? 'Επαναληπτικές' : 'Πανελλαδικές'));
  const years = f.year.values.map(Number);
  if (years.length > 0) parts.push((years.length === 1 ? 'Έτος: ' : 'Έτη: ') + yearRanges(years));
  const themes = f.theme.values;
  if (themes.length > 0) parts.push('Θέμα: ' + themes.join(', '));
  if (f.type.values.length > 0) parts.push('Τύπος: ' + list(f.type.labels.map(clean)));
  if (f.sec.values.length > 0) parts.push('Ενότητα: ' + list(f.sec.labels.map(clean)));
  if (f.tag.values.length > 0) parts.push('Έννοια: ' + list(f.tag.labels.map(clean)));
  const q = f.q.value.trim();
  if (q !== '') parts.push('Αναζήτηση: «' + q + '»');
  if (parts.length === 0) {
    const all = items.map((e) => e.exam_year);
    parts.push('Όλα τα θέματα · ' + yearRanges([Math.min(...all), Math.max(...all)]).replace(', ', ' - '));
  }
  return parts.join(' · ');
}

let renderToken = 0;
async function generate(items: Exercise[]): Promise<void> {
  const token = ++renderToken;
  const withAnswers = showAnsEl.checked;
  statementsPane.busy.hidden = false;
  answersPane.busy.hidden = !withAnswers;
  try {
    const keys = [...new Set(items.map((e) => examKey(e)))];
    const subtitle = filterSubtitle(items);
    const { mod, fonts } = await ensureAssets(keys);
    const [statements, answers] = await Promise.all([
      mod.buildStatementsPdf(items, sourceCache, fonts, { showYear: showYearEl.checked, subtitle }),
      withAnswers ? mod.buildAnswersPdf(items, typesById, fonts, { showYear: showYearEl.checked, subtitle }) : Promise.resolve(null),
    ]);
    if (token !== renderToken) return; // πρόλαβε νεότερη αλλαγή φίλτρων
    const suffix = filenameSuffix();
    setPane(statementsPane, statements, 'themata-' + suffix + '.pdf');
    setPane(answersPane, answers, answers === null ? '' : 'apantiseis-' + suffix + '.pdf');
    statusEl.hidden = true;
  } catch (err) {
    if (token !== renderToken) return;
    console.error(err);
    statusEl.hidden = false;
    statusEl.textContent = 'Δεν ήταν δυνατή η δημιουργία των PDF. Δοκίμασε ξανά.';
  } finally {
    if (token === renderToken) {
      statementsPane.busy.hidden = true;
      answersPane.busy.hidden = true;
    }
  }
}

let timer: number | undefined;
refresh = (): void => {
  updateFacets();
  const items = data.exercises.filter(matches);
  // Μόνο το μήνυμα «δεν βρέθηκαν» — το πλήθος των επιλεγμένων θεμάτων δεν εμφανίζεται.
  summaryEl.textContent = items.length === 0 ? 'Δεν βρέθηκαν θέματα με αυτά τα φίλτρα.' : '';
  summaryEl.hidden = items.length > 0;
  window.clearTimeout(timer);
  if (items.length === 0) {
    renderToken++;
    resultsEl.hidden = true;
    setPane(statementsPane, null, '');
    setPane(answersPane, null, '');
    return;
  }
  resultsEl.hidden = false;
  resultsEl.classList.toggle('pan-no-answers', !showAnsEl.checked);
  answersSectionEl.hidden = !showAnsEl.checked;
  timer = window.setTimeout(() => void generate(items), 250);
};

// ------------------------------------------------------------ Φίλτρα

interface FacetDef {
  key: FacetKey;
  ms: MultiSelect;
  searchable: boolean;
  opts: { option: MsOption; pred: (e: Exercise) => boolean; counted: boolean }[];
}
const facets: FacetDef[] = [];

/** Ξαναχτίζει τις επιλογές κάθε dropdown με μετρητές σύμφωνα με τα ΥΠΟΛΟΙΠΑ φίλτρα·
 *  επιλογές με 0 θέματα κρύβονται (εκτός αν είναι ήδη επιλεγμένες). */
function updateFacets(): void {
  for (const fc of facets) {
    const pool = data.exercises.filter((e) => matches(e, fc.key));
    const selected = new Set(fc.ms.values);
    const out: MsOption[] = [];
    for (const o of fc.opts) {
      const n = pool.filter(o.pred).length;
      if (n === 0 && !selected.has(o.option.value)) continue;
      out.push(o.counted ? { ...o.option, label: o.option.label + ' (' + n + ')' } : o.option);
    }
    fc.ms.setOptions(out, fc.searchable);
  }
}

function buildFilters(): void {
  const exs = data.exercises;

  facets.push({
    key: 'kind',
    ms: f.kind,
    searchable: false,
    opts: [
      { option: { value: 'regular', label: 'Πανελλαδικές εξετάσεις' }, pred: (e: Exercise) => kindOf(e) === 'regular', counted: true },
      { option: { value: 'repeat', label: 'Επαναληπτικές πανελλαδικές εξετάσεις' }, pred: (e: Exercise) => kindOf(e) === 'repeat', counted: true },
    ],
  });
  facets.push({
    key: 'year',
    ms: f.year,
    searchable: false,
    opts: [...new Set(exs.map((e) => e.exam_year))]
      .sort((a, b) => b - a)
      .map((y) => ({ option: { value: String(y), label: String(y) }, pred: (e: Exercise) => e.exam_year === y, counted: true })),
  });
  facets.push({
    key: 'theme',
    ms: f.theme,
    searchable: false,
    opts: THEMES.map((t) => ({ option: { value: t, label: 'Θέμα ' + t }, pred: (e: Exercise) => e.theme === t, counted: true })),
  });
  facets.push({
    key: 'type',
    ms: f.type,
    searchable: false,
    opts: data.exercise_types.map((t) => ({
      option: { value: t.id, label: t.label },
      pred: (e: Exercise) => e.exercise_type === t.id,
      counted: true,
    })),
  });
  facets.push({
    key: 'tag',
    ms: f.tag,
    searchable: true,
    opts: [...data.tags]
      .sort((a, b) => a.label.localeCompare(b.label, 'el'))
      .map((t) => ({ option: { value: String(t.id), label: t.label }, pred: (e: Exercise) => e.tag_ids.includes(t.id), counted: true })),
  });

  // Κεφάλαιο → ενότητες, μόνο όσα χρησιμοποιούνται από κάποιο θέμα.
  const used = new Set<string>();
  exs.forEach((e) =>
    e.book_sections.forEach((s) => {
      if (inSyllabus(s.book, s.section_number)) used.add(s.book + ':' + s.section_number);
    })
  );
  const secOpts: FacetDef['opts'] = [];
  data.books.forEach((bk) => {
    data.chapters
      .filter((c) => c.book === bk.id)
      .forEach((c) => {
        const secs = data.sections.filter(
          (s) => s.book === bk.id && s.chapter === c.number && used.has(s.book + ':' + s.number)
        );
        if (secs.length === 0) return;
        const group = (BOOK_SHORT[bk.id] ?? bk.id) + ' · Κεφ. ' + c.number + ' — ' + c.title;
        const chVal = 'ch:' + bk.id + ':' + c.number;
        secOpts.push({ option: { value: chVal, label: 'Όλο το κεφάλαιο ' + c.number, group }, pred: (e) => secMatch(e, chVal), counted: false });
        secs.forEach((s) => {
          const v = 'sec:' + bk.id + ':' + s.number;
          secOpts.push({ option: { value: v, label: s.number + ' ' + s.title, group }, pred: (e) => secMatch(e, v), counted: false });
        });
      });
  });
  facets.push({ key: 'sec', ms: f.sec, searchable: true, opts: secOpts });
  updateFacets();

  f.q.addEventListener('input', refresh);
  showYearEl.addEventListener('change', refresh);
  showAnsEl.addEventListener('change', refresh);
  byId<HTMLButtonElement>('pan-f-reset').addEventListener('click', () => {
    [f.year, f.theme, f.type, f.sec, f.tag].forEach((s) => s.clear());
    f.kind.select(KIND_DEFAULT);
    f.q.value = '';
    refresh();
  });
}

async function init(): Promise<void> {
  try {
    const r = await fetch('./panelladikes.json', { cache: 'no-cache' }); // ελέγχει πάντα με τον server (ETag), ώστε οι νέες εκδόσεις να φαίνονται αμέσως
    data = (await r.json()) as PanData;
  } catch {
    statusEl.textContent = 'Δεν ήταν δυνατή η φόρτωση των θεμάτων. Δοκίμασε ξανά αργότερα.';
    return;
  }
  data.tags.forEach((t) => tagsById.set(t.id, t.label));
  data.exercise_types.forEach((t) => (typesById[t.id] = t.label));
  // Νεότερο έτος πρώτα· μέσα στο έτος η σειρά του αρχείου.
  data.exercises = data.exercises
    .map((e, i) => ({ e, i }))
    .sort((a, b) => b.e.exam_year - a.e.exam_year || Number(kindOf(b.e) === 'repeat') - Number(kindOf(a.e) === 'repeat') || a.i - b.i)
    .map((x) => x.e);
  statusEl.hidden = true;
  f.kind.select(KIND_DEFAULT);
  buildFilters();
  refresh();
}

// ------------------------------------------------------------ Αποτρεπτικά μέτρα
// Ίδια λογική με το methodologia.html: δεξί κλικ, Ctrl/Cmd+C/A/S/P, attribution σε ό,τι
// αντιγραφεί. ΔΕΝ είναι πραγματική προστασία (screenshot/dev tools μένουν δυνατά) —
// σκοπός είναι να μην είναι η αντιγραφή/εκτύπωση ένα κλικ μακριά.
function isTextField(t: EventTarget | null): boolean {
  return t instanceof HTMLInputElement || t instanceof HTMLTextAreaElement;
}
const SOURCE_NOTE = '\n\nΠηγή: https://aeppglossa.gr/aepp-panelladikes.html';
document.addEventListener('contextmenu', (e) => {
  if (!isTextField(e.target)) e.preventDefault();
});
document.addEventListener('dragstart', (e) => e.preventDefault());
document.addEventListener('keydown', (e) => {
  const key = e.key ? e.key.toLowerCase() : '';
  if ((e.ctrlKey || e.metaKey) && ['c', 'a', 's', 'p'].includes(key) && !isTextField(e.target)) e.preventDefault();
});
document.addEventListener('copy', (e) => {
  if (isTextField(e.target)) return;
  const selection = window.getSelection ? window.getSelection()?.toString() ?? '' : '';
  if (selection === '' || e.clipboardData === null) return;
  e.clipboardData.setData('text/plain', selection + SOURCE_NOTE);
  e.preventDefault();
});

void init();
